import { State, Action, StateContext, createPropertySelectors, Selector } from '@ngxs/store';
import { inject, Injectable } from '@angular/core';
import { map } from 'rxjs';
import {
    keyBy as _keyBy
} from 'lodash-es';

import { GameState } from '../game/game.state';
import { IGame } from '../game/game.models';

import { ITeam, ITeamStateModel } from './team.models';
import { teamStateDefaults } from './team.utils';
import { TeamService } from './team.service';
import { FetchTeamsForLeague } from './team.actions';

import { QueryParamsChanged } from 'src/services/query-params/query-params.actions';

@Injectable()
@State<ITeamStateModel>({
    name: teamStateDefaults.sliceName,
    defaults: teamStateDefaults.defaults
})
export class TeamState {
    public static selectors = createPropertySelectors<ITeamStateModel>(TeamState);

    private _teamService = inject(TeamService);

    /**
     * Returns the team object that is selected
     * @param teamsById
     * @param selectedTeamId
     * @returns
     */
    @Selector([TeamState.selectors.teamsById, TeamState.selectors.selectedTeamId])
    public static selectedTeam(
        teamsById: Record<string, ITeam> | undefined,
        selectedTeamId: string | undefined
    ): ITeam | undefined {
        if (!selectedTeamId || !teamsById) {
            return;
        }
        return teamsById[selectedTeamId];
    }

    /**
     * Check if current team is a home or away team.
     *
     * @param selectedTeamId
     * @param game
     * @returns
     */
    @Selector([TeamState.selectors.selectedTeamId, GameState.selectors.game])
    public static isHomeTeam(
        selectedTeamId: string | undefined,
        game: IGame | undefined
    ): boolean {
        if (!game) {
            return false;
        }

        return selectedTeamId === game.homeTeamId;
    }

    /**
     * Returns the home team object for the game being processed
     * @param teamsById
     * @param game
     * @returns
     */
    @Selector([TeamState.selectors.teamsById, GameState.selectors.game])
    public static homeTeam(
        teamsById: Record<string, ITeam> | undefined,
        game: IGame | undefined
    ): ITeam | undefined {
        if (!game || !teamsById) {
            return;
        }

        return teamsById[game.homeTeamId];
    }

    /**
     * Returns the away team object for the game being processed
     * @param teamsById
     * @param game
     * @returns
     */
    @Selector([TeamState.selectors.teamsById, GameState.selectors.game])
    public static awayTeam(
        teamsById: Record<string, ITeam> | undefined,
        game: IGame | undefined
    ): ITeam | undefined {
        if (!game || !teamsById) {
            return;
        }

        return teamsById[game.awayTeamId];
    }

    @Action(FetchTeamsForLeague)
    private _fetchTeams(ctx: StateContext<ITeamStateModel>, { leagueId }: FetchTeamsForLeague) {
        return this._teamService.getTeamsApi({ leagueId: [leagueId] }).pipe(
            map(teams => {
                return ctx.patchState({
                    teamsById: _keyBy(teams, team => team.id)
                });
            })
        );
    }

    @Action(QueryParamsChanged)
    private _queryParamsChanged(ctx: StateContext<ITeamStateModel>, { queryParams }: QueryParamsChanged) {
        if (queryParams.teamid && ctx.getState().selectedTeamId !== queryParams.teamid) {
            return ctx.patchState({
                selectedTeamId: queryParams.teamid
            });
        }

        return;
    }
}
